import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mic2, Music4, ShieldCheck, Sparkles } from "lucide-react";
import { ARTIST_SPOTLIGHT } from "../lib/site";

const DETAIL_TILES = [
  {
    icon: Mic2,
    label: "Role",
    value: ARTIST_SPOTLIGHT.role,
  },
  {
    icon: Sparkles,
    label: "Stage Credential",
    value: ARTIST_SPOTLIGHT.credential,
  },
  {
    icon: Music4,
    label: "Signature Energy",
    value: "Sufi warmth, ceremony control and premium reception lift.",
  },
];

export const ArtistSpotlight = () => {
  return (
    <section
      data-testid="artist-spotlight-section"
      className="relative overflow-hidden bg-[#080808] py-24 md:py-32 lg:py-40"
    >
      <div className="pointer-events-none absolute left-[-8%] top-[14%] h-[280px] w-[280px] rounded-full bg-[#d4af37]/10 blur-3xl" />
      <div className="pointer-events-none absolute right-[-8%] bottom-[10%] h-[320px] w-[320px] rounded-full bg-[#7a3312]/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-6 md:px-10 lg:grid-cols-12 lg:gap-16 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <div className="relative overflow-hidden border border-[#d4af37]/20 bg-[#100d0a] p-3 shadow-[0_30px_90px_-40px_rgba(212,175,55,0.38)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_8%,rgba(212,175,55,0.12),transparent_42%)]" />
            <img
              src={ARTIST_SPOTLIGHT.image}
              alt={ARTIST_SPOTLIGHT.name}
              className="relative h-full w-full object-cover"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-[linear-gradient(180deg,transparent,rgba(8,8,8,0.84))]" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.85, delay: 0.06 }}
          className="lg:col-span-7"
        >
          <div className="overline">— Lead Vocalist</div>
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.04] md:text-5xl lg:text-6xl">
            Meet <em className="gold-gradient-text not-italic">Deepak Malik</em>, the voice that gives the celebration its center.
          </h2>

          <div className="gold-hairline my-8 max-w-xs" />

          <div className="max-w-3xl space-y-5 text-base font-light leading-relaxed text-white/66 md:text-lg">
            <p>{ARTIST_SPOTLIGHT.intro}</p>
            <p>{ARTIST_SPOTLIGHT.description}</p>
          </div>

          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/25 bg-[#d4af37]/8 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
            <ShieldCheck size={14} />
            {ARTIST_SPOTLIGHT.credential}
          </div>

          <div className="mt-10 grid gap-3 md:grid-cols-3">
            {DETAIL_TILES.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className="border border-white/10 bg-white/[0.03] p-5"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#d4af37]">
                    <Icon size={14} />
                    {item.label}
                  </div>
                  <div className="mt-3 text-sm font-light leading-relaxed text-white/72">
                    {item.value}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 border border-white/10 bg-[linear-gradient(180deg,rgba(212,175,55,0.06)_0%,rgba(10,10,10,0.94)_100%)] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
              Why he matters in the room
            </div>
            <div className="mt-5 grid gap-3">
              {ARTIST_SPOTLIGHT.strengths.map((point) => (
                <div
                  key={point}
                  className="flex items-start gap-3 border-t border-white/8 pt-4 first:border-t-0 first:pt-0"
                >
                  <div className="mt-0.5 text-[#d4af37]">✦</div>
                  <div className="text-sm font-light leading-relaxed text-white/68 md:text-base">
                    {point}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <a
            href="#bookings"
            className="gold-button mt-10 inline-flex items-center gap-2 rounded-sm px-8 py-4 text-xs font-semibold uppercase tracking-[0.3em]"
          >
            For Enquiry <ArrowRight size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ArtistSpotlight;
